import { useEffect, useCallback, useRef } from "react";
import { coreWebVitals } from "@/lib/performanceBudgets";
import { supabase } from "@/integrations/supabase/client";

interface PerformanceMetrics {
  FCP?: number;
  LCP?: number;
  FID?: number;
  CLS?: number;
  TTFB?: number;
  INP?: number;
}

// Get or create session ID for grouping metrics
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem("perf_session_id");
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem("perf_session_id", sessionId);
  }
  return sessionId;
};

/**
 * Hook to monitor Core Web Vitals and report performance issues
 */
export const usePerformanceMonitor = () => {
  const reportedMetricsRef = useRef<Set<string>>(new Set());

  const reportToDatabase = useCallback(async (name: string, value: number, rating: "good" | "needs-improvement" | "poor") => {
    // Prevent duplicate reports for same metric in same session
    const metricKey = `${name}-${window.location.pathname}`;
    if (reportedMetricsRef.current.has(metricKey)) return;
    reportedMetricsRef.current.add(metricKey);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) return; // Only report for authenticated users

      await supabase.from("web_vitals").insert({
        user_id: user.id,
        session_id: getSessionId(),
        page_url: window.location.pathname,
        metric_name: name,
        metric_value: value,
        rating,
      });
    } catch (error) {
      // Silently fail - don't impact user experience
      if (import.meta.env.DEV) {
        console.warn("[Performance] Failed to report metric:", error);
      }
    }
  }, []);

  const reportMetric = useCallback((name: string, value: number, rating: "good" | "needs-improvement" | "poor") => {
    // Log to console in development
    if (import.meta.env.DEV) {
      const emoji = rating === "good" ? "✅" : rating === "needs-improvement" ? "⚠️" : "❌";
      console.log(`[Performance] ${emoji} ${name}: ${value.toFixed(1)}ms (${rating})`);
    }

    // Report to database in production
    if (!import.meta.env.DEV) {
      reportToDatabase(name, value, rating);
    }
  }, [reportToDatabase]);

  const getRating = useCallback((name: keyof typeof coreWebVitals, value: number): "good" | "needs-improvement" | "poor" => {
    const threshold = coreWebVitals[name];
    if (name === "CLS") {
      // CLS is not in ms
      if (value <= threshold) return "good";
      if (value <= threshold * 2.5) return "needs-improvement";
      return "poor";
    }
    if (value <= threshold) return "good";
    if (value <= threshold * 2) return "needs-improvement";
    return "poor";
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const metrics: PerformanceMetrics = {};

    // First Contentful Paint
    const fcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const fcp = entries.find((e) => e.name === "first-contentful-paint");
      if (fcp) {
        metrics.FCP = fcp.startTime;
        reportMetric("FCP", fcp.startTime, getRating("FCP", fcp.startTime));
      }
    });

    // Largest Contentful Paint
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      if (lastEntry) {
        metrics.LCP = lastEntry.startTime;
        reportMetric("LCP", lastEntry.startTime, getRating("LCP", lastEntry.startTime));
      }
    });

    // Cumulative Layout Shift
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!(entry as PerformanceEntry & { hadRecentInput?: boolean }).hadRecentInput) {
          clsValue += (entry as PerformanceEntry & { value: number }).value;
        }
      }
      metrics.CLS = clsValue;
    });

    // First Input Delay
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const firstEntry = entries[0] as PerformanceEntry & { processingStart: number };
      if (firstEntry) {
        const fid = firstEntry.processingStart - firstEntry.startTime;
        metrics.FID = fid;
        reportMetric("FID", fid, getRating("FID", fid));
      }
    });

    try {
      fcpObserver.observe({ entryTypes: ["paint"] });
      lcpObserver.observe({ entryTypes: ["largest-contentful-paint"] });
      clsObserver.observe({ entryTypes: ["layout-shift"] });
      fidObserver.observe({ entryTypes: ["first-input"] });
    } catch {
      // Some browsers don't support all entry types
    }

    // Report CLS on page hide
    const reportCLS = () => {
      if (metrics.CLS !== undefined) {
        reportMetric("CLS", metrics.CLS, getRating("CLS", metrics.CLS));
      }
    };

    window.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        reportCLS();
      }
    });

    return () => {
      fcpObserver.disconnect();
      lcpObserver.disconnect();
      clsObserver.disconnect();
      fidObserver.disconnect();
    };
  }, [reportMetric, getRating]);
};

/**
 * Get navigation timing metrics
 */
export const getNavigationTiming = (): { ttfb: number; domLoad: number; fullLoad: number } | null => {
  if (typeof window === "undefined") return null;
  
  const navigation = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
  if (!navigation) return null;

  return {
    ttfb: navigation.responseStart - navigation.requestStart,
    domLoad: navigation.domContentLoadedEventEnd - navigation.startTime,
    fullLoad: navigation.loadEventEnd - navigation.startTime,
  };
};
